import React, { forwardRef, useState, useMemo, useCallback } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { uniqBy, isEmpty, map } from "lodash";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import { Form, message } from "antd";

const CycodeCSPMMappingForm = forwardRef(function CycodeCSPMMappingForm(
  { disableProjectFields = false, ...props },
  ref
) {
  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { repositoryIds, repositoryNames, projectId, projectName } = props.record;
      const repoIdArray = repositoryIds?.split(",");
      const repoNameArray = repositoryNames?.split(",");
      const projectIdArray = isEmpty(projectId) ? [] : projectId?.split(",");
      const projectNameArray = isEmpty(projectName) ? [] : projectName?.split(",");
      return map(repoIdArray, (id, index) => ({
        id,
        name: repoNameArray[index],
        otherProperties : { projectId: isEmpty(projectIdArray[index]) ? null : projectIdArray[index], projectName: isEmpty(projectNameArray[index]) ? null : projectNameArray[index] }
      }));
    }
    return [];
  });
  const [loading, setLoading] = useState(true);

  const [form] = Form.useForm();

  const setOptionsFromPagination = (project) => {
    /**
     * The reason for this change is that we haven't yet completed the backend migration for multi-mapping.
     * For existing entries, projectId is null because we sent projectId as null to the backend.
     * However, the API response includes the correct projectId and project name under the otherProperties.
     * Therefore, we are prioritizing the API response to ensure we receive the correct projectId and project name.
     */
    setProjects((prev) => uniqBy([...project, ...prev], "id"));
  };

  const fetchRepos = useCallback(
    (params) => {
      let { promise, cancel } =
        ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
          login_id: props.loginConfigId,
          type: "repository",
          ...params,
        });
      promise
        .then((response) => {
          const { projects } = response;
          setOptionsFromPagination(projects);
        })
        .catch((err) => {
          message.error("Problem fetching repositories");
        })
        .finally(() => setLoading(false));
      promise = promise.then((res) => res.projects);
      return { promise, cancel };
    },
    [props.loginConfigId, props.toolName]
  );

  const getExtraFieldsData = (config) => {
    const { repositoryIds, cspm, cspmCheck, repositoryCheck, fetchLeaks } = config;
    if (!repositoryCheck && !cspmCheck && !fetchLeaks) {
      message.error("Please select one of the fields");
      throw new Error("Please select one of the fields");
    }

    let extraFieldObject = {
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
    if (repositoryIds && repositoryCheck) {
      const data = projects.reduce(
        (acc, cv) => {
          const { id, name, otherProperties: {projectId, projectName} } = cv;
          if (repositoryIds?.includes(id)) {
            acc.id.push(id);
            acc.name.push(name);
            acc.projectId.push(projectId);
            acc.projectName.push(projectName);
          }
          return acc;
        },
        { id: [], name: [], projectId: [], projectName: [] },
      );

      extraFieldObject.repositoryIds = data.id.join();
      extraFieldObject.repositoryNames = data.name.join();
      extraFieldObject.projectId = data.projectId.join();
      extraFieldObject.projectName = data.projectName.join();
    }
    if (cspm && cspmCheck) {
      extraFieldObject.type = "CSPM";
      extraFieldObject.accountId = "all";
      extraFieldObject.accountName = "all";
    }
    extraFieldObject.fetchLeaks = config.fetchLeaks;

    return extraFieldObject;
  };

  const repositoryRender = useCallback(
    (listField) => {
      const repositoryFields = [
        {
          name: "repositoryIds",
          colSpan: "12",
          loading: loading,
          type: FieldTypes.MULTI_SELECT,
          label: "Repository",
          options: projects,
          required: true,
          disabled: disableProjectFields,
          props: {
            autoFocus: !disableProjectFields,
            api: fetchRepos,
          },
        },
      ];

      const repositoryChecked =
        form.getFieldValue("configurations")[listField.name]?.repositoryCheck;
      return repositoryChecked
        ? generateFormFields(repositoryFields, 10, listField)
        : null;
    },
    [disableProjectFields, form, fetchRepos, loading, projects]
  );

  const cspmRender = useCallback(
    (listField) => {
      const cspmFields = [
        {
          name: "cspm",
          label: "Fetch all Cloud and Container Security Findings",
          type: FieldTypes.BOOLEAN,
          inline: true,
          disabled: disableProjectFields,
          colSpan: 12,
          rules: [
            {
         validator: (rule, value) => {
                const config = form.getFieldValue("configurations")[listField.name];
                if (
                   !config.fetchLeaks && (
                  (!value && !config.repositoryIds) ||
                  (!config.cspmCheck && !config.repositoryCheck)
                   )
                ) {

                  return Promise.reject("Please select one of the fields");
                }
                return Promise.resolve();
              },
            }
          ],
        },
      ];

      const cspmChecked =
        form.getFieldValue("configurations")[listField.name]?.cspmCheck;

      return cspmChecked ? generateFormFields(cspmFields, 10, listField) : null;
    },
    [disableProjectFields, form]
  );

  const extraFields = useMemo(
    () => [
      {
        name: "repositoryCheck",
        label: "Repositories",
        type: FieldTypes.BOOLEAN,
        disabled: disableProjectFields,
        inline: true,
      },
      {
        name: (listField) => [listField.name, "repoList"],
        render: repositoryRender,
      },
      {
        name: "cspmCheck",
        label: "CSPM",
        type: FieldTypes.BOOLEAN,
        inline: true,
        disabled: disableProjectFields,
      },
      {
        name: (listField) => [listField.name, "cspmList"],
        render: cspmRender,
      },
      {
        name: "fetchLeaks",
        label: "Ingest Leaks Violations",
        type: FieldTypes.BOOLEAN,
        inline: true,
        disabled: disableProjectFields,
      }
    ],
    [disableProjectFields, cspmRender, repositoryRender]
  );

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { accountId, repositoryIds } = config;
    return {
      fetchLeaks: config.fetchLeaks === "true",
      cspm: accountId === "all",
      cspmCheck: accountId === "all",
      repositoryCheck: !isEmpty(repositoryIds),
      repositoryIds: isEmpty(repositoryIds)
        ? undefined
        : repositoryIds?.split(","),
      //send the property received in config
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  }, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default CycodeCSPMMappingForm;
